
import { useState, useCallback } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { DRIVERS_2026 } from '../data/f1data2026';

// ── Shared sub-components ─────────────────────────────────────────────────

function DriverGrid({ label, value, onChange, exclude=[], drivers }) {
  const src = drivers && drivers.length > 0 ? drivers : DRIVERS_2026;
  return (
    <div style={{display:'flex',flexDirection:'column',gap:6}}>
      <div style={{fontSize:'0.58rem',letterSpacing:'0.15em',color:'#4a6a9e',textTransform:'uppercase'}}>{label}</div>
      <div className="f1-pred-driver-grid">
        {src.map(d => {
          const blocked = exclude.includes(d.acronym) && d.acronym !== value;
          return (
            <button
              key={d.num}
              className={'f1-pred-driver-btn' + (value === d.acronym ? ' selected' : '')}
              onClick={() => onChange(value === d.acronym ? null : d.acronym)}
              disabled={blocked}
              style={blocked ? {opacity:0.25,cursor:'not-allowed'} : {}}
            >
              <div style={{width:3,height:22,borderRadius:2,background:d.colour,flexShrink:0}}/>
              <span className="f1-driver-num">{d.num}</span>
              <div style={{display:'flex',flexDirection:'column',lineHeight:1.2}}>
                <span style={{fontSize:'0.78rem',fontWeight:700,color:'#e8edf8'}}>{d.acronym}</span>
                <span style={{fontSize:'0.52rem',color:'#4a6a9e'}}>{d.team.split(' ').pop()}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LeagueTable({ table, myName }) {
  if (!table || table.length === 0) return null;
  return (
    <div className="pq-scoreboard" style={{width:'100%'}}>
      <div className="pq-score-hdr">Standings</div>
      {table.map((row, i) => (
        <div key={row.name} className="pq-score-row"
          style={{background: row.name === myName ? 'rgba(232,0,45,0.06)' : 'transparent',
            flexDirection:'column', alignItems:'flex-start', gap:2}}>
          <div style={{display:'flex',width:'100%',alignItems:'center',gap:8}}>
            <span className="pq-score-pos">{i===0?'🥇':i===1?'🥈':i===2?'🥉':('#'+(i+1))}</span>
            <span className="pq-score-name" style={{flex:1,color:row.name===myName?'#e8002d':'#e8edf8'}}>
              {row.name}{row.name===myName?' (you)':''}
            </span>
            <span className="pq-score-pts">{row.pts} pts</span>
          </div>
          {row.name === myName && row.breakdown?.length > 0 && (
            <div style={{paddingLeft:28,display:'flex',flexWrap:'wrap',gap:3,marginTop:2}}>
              {row.breakdown.map((b,j) => (
                <span key={j} style={{fontSize:'0.57rem',color:'#4ade80',background:'rgba(34,197,94,0.1)',
                  borderRadius:4,padding:'1px 6px'}}>+{b.pts} {b.desc}</span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────

export default function F1PredictorPlayer({ onBack }) {
  // Navigation
  const [phase, setPhase]             = useState('join');
  // Join form
  const [codeInput, setCodeInput]     = useState('');
  const [nameInput, setNameInput]     = useState('');
  const [error, setError]             = useState('');
  // Session state
  const [myName, setMyName]           = useState('');
  const [f1Session, setF1Session]     = useState(null);
  const [sessionCode, setSessionCode] = useState('');
  const [serverState, setServerState] = useState(null);
  const [confirmed, setConfirmed]     = useState(false);
  const [liveDrivers, setLiveDrivers] = useState([]);
  // Prediction fields — winner IS P1, podium is P2+P3
  const [p1, setP1]       = useState(null);   // race winner / P1
  const [p2, setP2]       = useState(null);   // P2
  const [p3, setP3]       = useState(null);   // P3
  const [fl, setFl]       = useState(null);   // fastest lap
  const [sc, setSc]       = useState(null);   // safety car boolean
  const [ret, setRet]     = useState(null);   // first retirement
  const [pits, setPits]   = useState('');     // total pit stops

  const restorePrediction = (pred) => {
    const podium = pred.podium || [];
    setP1(pred.winner || podium[0] || null);
    setP2(podium[1] || null);
    setP3(podium[2] || null);
    setFl(pred.fastestLap || null);
    setSc(pred.safetyCar ?? null);
    setRet(pred.firstRetirement || null);
    setPits(pred.totalPitStops !== undefined ? String(pred.totalPitStops) : '');
  };

  const onMsg = useCallback((msg) => {
    if (msg.type === 'F1_PREDICTOR_JOINED' || msg.type === 'F1_PREDICTOR_REJOINED') {
      setMyName(msg.name);
      setF1Session(msg.session);
      setSessionCode(msg.code);
      setPhase('predicting');
      return;
    }
    if (msg.type === 'F1_PREDICTOR_CONFIRMED') {
      setConfirmed(true);
      setPhase('submitted');
      return;
    }
    if (msg.type === 'F1_PREDICTOR_STATE') {
      setServerState(msg);
      // Update live driver list from snapshot
      const lb = msg.snapshot?.leaderboard || [];
      if (lb.length > 0) {
        setLiveDrivers(lb.map(d => ({
          num:     d.driverNumber,
          acronym: d.acronym || d.nameAcronym,
          name:    d.fullName || d.acronym,
          team:    d.teamName,
          colour:  d.teamColour,
        })));
      }
      // Restore own prediction on rejoin
      const me = msg.predictions?.[msg.name] || msg.predictions?.[myName];
      if (me && !confirmed) {
        restorePrediction(me);
        setConfirmed(true);
      }
      // Phase transitions
      if (msg.status === 'finished') { setPhase('finished'); return; }
      if (msg.status === 'live')     { setPhase('live');     return; }
      return;
    }
    if (msg.type === 'ERROR') {
      setError(msg.message);
    }
  }, [myName, confirmed]); // eslint-disable-line

  const { send } = useWebSocket(onMsg);

  const join = () => {
    const code = codeInput.trim().toUpperCase();
    const name = nameInput.trim();
    if (code.length !== 4) { setError('Enter the 4-letter code'); return; }
    if (!name)             { setError('Enter your name');          return; }
    setError('');
    send('F1_PREDICTOR_JOIN', { code, name });
  };

  const submit = () => {
    if (!p1) { setError('Pick a race winner'); return; }
    setError('');
    send('F1_PREDICTOR_SUBMIT', {
      winner:          p1,
      podium:          [p1, p2, p3].filter(Boolean),
      fastestLap:      fl,
      safetyCar:       sc,
      firstRetirement: ret,
      totalPitStops:   pits !== '' ? parseInt(pits) : undefined,
    });
  };

  const drivers    = liveDrivers.length > 0 ? liveDrivers : DRIVERS_2026;
  const table      = serverState?.table      || [];
  const result     = serverState?.result     || null;
  const lb         = serverState?.snapshot?.leaderboard || [];
  const myPred     = serverState?.predictions?.[myName] || null;

  // ── JOIN ──────────────────────────────────────────────────────────────
  if (phase === 'join') return (
    <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="g-hdr f1-hdr">
        <button className="g-back" onClick={onBack}>← Menu</button>
        <div className="g-title" style={{color:'#e8002d'}}>JOIN F1 PREDICTOR</div>
      </div>
      <div className="join-wrap"><div className="join-card">
        <div style={{fontSize:'1.8rem',fontFamily:"'Bebas Neue',sans-serif",color:'#e8002d',letterSpacing:'0.1em'}}>🏎️ JOIN</div>
        <p style={{fontSize:'0.74rem',color:'#8ba3cc'}}>Enter the session code from the host screen</p>
        <input className="join-input" style={{borderColor:'#3a0a10',color:'#e8002d'}}
          value={codeInput} onChange={e => setCodeInput(e.target.value.toUpperCase().slice(0,4))}
          placeholder="CODE" maxLength={4}/>
        <input className="join-name-input"
          value={nameInput} onChange={e => setNameInput(e.target.value.slice(0,16))}
          placeholder="Your name"/>
        {error && <div className="join-error">{error}</div>}
        <button onClick={join} style={{padding:'11px 34px',border:'none',borderRadius:10,
          background:'linear-gradient(135deg,#e8002d,#8b0015)',color:'#fff',
          fontFamily:"'Bebas Neue',sans-serif",fontSize:'1.2rem',letterSpacing:'0.1em',cursor:'pointer'}}>
          JOIN SESSION
        </button>
        <p style={{fontSize:'0.64rem',color:'#3a5a6e',lineHeight:1.5}}>
          Leave and rejoin anytime — predictions persist for 24h
        </p>
      </div></div>
    </div>
  );

  // ── PREDICTING / SUBMITTED ────────────────────────────────────────────
  if (phase === 'predicting' || phase === 'submitted') return (
    <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="g-hdr f1-hdr">
        <button className="g-back" onClick={onBack}>← Leave</button>
        <div className="g-title" style={{color:'#e8002d'}}>F1 PREDICT</div>
        <div className="g-pills">
          <div className="g-pill f1-pill"><span className="g-plbl">Code</span><span className="g-pval" style={{color:'#e8002d'}}>{sessionCode}</span></div>
        </div>
      </div>
      <div className="game-body" style={{maxWidth:520}}>

        {/* Session info */}
        {f1Session && (
          <div style={{background:'#0f1e38',border:'1px solid #3a0a10',borderRadius:12,padding:'14px 16px',textAlign:'center'}}>
            <div style={{fontSize:'0.58rem',color:'#4a6a9e',textTransform:'uppercase',letterSpacing:'0.15em'}}>
              {f1Session.meetingName} · {f1Session.countryName}
            </div>
            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:'1.6rem',color:'#e8002d',marginTop:4}}>
              {f1Session.sessionName}
            </div>
            <div style={{fontSize:'0.62rem',color:'#4a6a9e',marginTop:2}}>
              {new Date(f1Session.dateStart).toLocaleString([],{weekday:'short',day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}
            </div>
          </div>
        )}

        {/* Saved confirmation */}
        {confirmed && (
          <div style={{background:'rgba(232,0,45,0.08)',border:'1px solid #e8002d',borderRadius:10,
            padding:'10px 14px',textAlign:'center',fontSize:'0.78rem',color:'#e8002d',fontWeight:600}}>
            ✓ Prediction saved · You can leave and rejoin with code <strong>{sessionCode}</strong>
          </div>
        )}

        {/* P1 — Race Winner */}
        <DriverGrid label="🏆 Race Winner (P1) *" value={p1} onChange={setP1}
          exclude={[p2,p3].filter(Boolean)} drivers={drivers}/>

        {/* P2 */}
        <DriverGrid label="🥈 2nd Place (P2)" value={p2} onChange={setP2}
          exclude={[p1,p3].filter(Boolean)} drivers={drivers}/>

        {/* P3 */}
        <DriverGrid label="🥉 3rd Place (P3)" value={p3} onChange={setP3}
          exclude={[p1,p2].filter(Boolean)} drivers={drivers}/>

        {/* Fastest Lap */}
        <DriverGrid label="⚡ Fastest Lap" value={fl} onChange={setFl}
          exclude={[]} drivers={drivers}/>

        {/* First Retirement */}
        <DriverGrid label="💥 First Retirement" value={ret} onChange={setRet}
          exclude={[]} drivers={drivers}/>

        {/* Safety Car */}
        <div style={{display:'flex',flexDirection:'column',gap:6}}>
          <div style={{fontSize:'0.58rem',letterSpacing:'0.15em',color:'#4a6a9e',textTransform:'uppercase'}}>
            🚗 Safety Car deployed?
          </div>
          <div style={{display:'flex',gap:8}}>
            {[{v:true,l:'Yes 🚗'},{v:false,l:'No 🚫'}].map(({v,l}) => (
              <button key={String(v)} onClick={() => setSc(sc === v ? null : v)}
                style={{flex:1,padding:'9px',borderRadius:9,border:'2px solid',cursor:'pointer',
                  fontFamily:'Inter,sans-serif',fontSize:'0.82rem',fontWeight:600,
                  borderColor: sc===v ? '#e8002d':'#1e3a6e',
                  background:  sc===v ? 'rgba(232,0,45,0.1)':'#0a1628',
                  color:       sc===v ? '#e8002d':'#8ba3cc'}}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Total pit stops */}
        <div style={{display:'flex',flexDirection:'column',gap:6}}>
          <div style={{fontSize:'0.58rem',letterSpacing:'0.15em',color:'#4a6a9e',textTransform:'uppercase'}}>
            🔧 Total pit stops (all drivers combined) — ±3 scores 2pts
          </div>
          <input type="number" min="0" max="120" value={pits}
            onChange={e => setPits(e.target.value)} placeholder="e.g. 42"
            style={{width:90,padding:'8px 12px',borderRadius:8,background:'#0a1628',
              border:'1px solid #3a0a10',color:'#e8002d',fontFamily:"'Bebas Neue',sans-serif",
              fontSize:'1.4rem',outline:'none',textAlign:'center'}}/>
        </div>

        {/* Scoring key */}
        <div style={{fontSize:'0.6rem',color:'#3a5a6e',lineHeight:1.7,borderTop:'1px solid #1e3a6e',paddingTop:10}}>
          Scoring: <span style={{color:'#e8edf8'}}>
            Winner (P1) 10pts · P2 correct 3pts · P3 correct 3pts · Exact P1–P3 order +5pts bonus ·
            Fastest lap 5pts · Safety car 3pts · First retirement 5pts · Pit stops ±3 = 2pts
          </span>
        </div>

        {error && <div className="join-error">{error}</div>}

        <button onClick={submit} style={{padding:'11px',border:'none',borderRadius:10,width:'100%',
          background:'linear-gradient(135deg,#e8002d,#8b0015)',color:'#fff',
          fontFamily:"'Bebas Neue',sans-serif",fontSize:'1.2rem',letterSpacing:'0.1em',cursor:'pointer'}}>
          {confirmed ? 'UPDATE PREDICTION' : 'SUBMIT PREDICTION'}
        </button>

        <LeagueTable table={table} myName={myName}/>
      </div>
    </div>
  );

  // ── LIVE ──────────────────────────────────────────────────────────────
  if (phase === 'live') return (
    <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="g-hdr f1-hdr">
        <button className="g-back" onClick={onBack}>← Leave</button>
        <div className="g-title" style={{color:'#e8002d'}}>● LIVE</div>
        <div className="g-pills">
          <div className="g-pill f1-pill"><span className="g-plbl">Code</span><span className="g-pval" style={{color:'#e8002d'}}>{sessionCode}</span></div>
        </div>
      </div>
      <div className="game-body" style={{maxWidth:480}}>
        {/* Live race order */}
        {lb.length > 0 && (
          <div style={{background:'#0a1628',border:'1px solid #3a0a10',borderRadius:12,padding:'14px'}}>
            <div style={{fontSize:'0.55rem',color:'#e8002d',letterSpacing:'0.15em',textTransform:'uppercase',
              marginBottom:8,animation:'pulse 1.5s ease infinite'}}>● Live Race Order</div>
            {lb.slice(0,5).map((d,i) => (
              <div key={d.driverNumber||i} style={{display:'flex',alignItems:'center',gap:8,
                padding:'5px 0',borderBottom:'1px solid #0f1e38'}}>
                <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:'1.1rem',width:20,
                  color:i===0?'#ffd700':i===1?'#c0c0c0':i===2?'#cd7f32':'#3a5a8e'}}>
                  {d.position||i+1}
                </span>
                <div style={{width:3,height:22,borderRadius:2,background:d.teamColour||'#3a5a8e'}}/>
                <span style={{flex:1,fontSize:'0.8rem',fontWeight:700,color:'#e8edf8'}}>{d.acronym}</span>
                {d.inPit && <span className="f1-pit-badge">PIT</span>}
              </div>
            ))}
          </div>
        )}

        {/* My prediction summary */}
        {myPred && (
          <div style={{background:'#0f1e38',border:'1px solid #3a0a10',borderRadius:10,padding:'12px 14px'}}>
            <div style={{fontSize:'0.55rem',color:'#4a6a9e',textTransform:'uppercase',letterSpacing:'0.15em',marginBottom:6}}>
              Your Prediction
            </div>
            <div style={{fontSize:'0.84rem',color:'#ffd700',fontWeight:700}}>🏆 {myPred.winner}</div>
            {myPred.podium?.length > 1 && (
              <div style={{fontSize:'0.74rem',color:'#c0c0c0',marginTop:3}}>
                🥈 {myPred.podium[1]} · 🥉 {myPred.podium[2] || '?'}
              </div>
            )}
            {myPred.safetyCar !== null && myPred.safetyCar !== undefined && (
              <div style={{fontSize:'0.7rem',color:'#f5c518',marginTop:2}}>
                {myPred.safetyCar ? '🚗 SC: Yes' : '🚫 SC: No'}
              </div>
            )}
          </div>
        )}

        <LeagueTable table={table} myName={myName}/>
        <p style={{fontSize:'0.64rem',color:'#4a6a9e',textAlign:'center'}}>
          Updates every 30s · Rejoin with code <strong style={{color:'#e8002d'}}>{sessionCode}</strong>
        </p>
      </div>
    </div>
  );

  // ── FINISHED ──────────────────────────────────────────────────────────
  if (phase === 'finished') return (
    <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="g-hdr f1-hdr">
        <button className="g-back" onClick={onBack}>← Menu</button>
        <div className="g-title" style={{color:'#ffd700'}}>CHEQUERED FLAG 🏁</div>
      </div>
      <div className="game-body" style={{maxWidth:480}}>
        {result && (
          <div style={{background:'#0a1628',border:'1px solid #3a0a10',borderRadius:12,padding:'16px',textAlign:'center'}}>
            <div style={{fontSize:'0.55rem',color:'#ffd700',letterSpacing:'0.15em',textTransform:'uppercase',marginBottom:8}}>
              Final Result
            </div>
            {result.winner && (
              <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:'2rem',color:'#ffd700'}}>
                🏆 {result.winner}
              </div>
            )}
            {result.podium?.length > 0 && (
              <div style={{fontSize:'0.8rem',color:'#c0c0c0',marginTop:4}}>
                🥇 {result.podium[0]} · 🥈 {result.podium[1]} · 🥉 {result.podium[2]}
              </div>
            )}
            {result.safetyCar !== undefined && (
              <div style={{fontSize:'0.7rem',color:'#f5c518',marginTop:4}}>
                {result.safetyCar ? '🚗 Safety Car deployed' : '🚫 No Safety Car'}
              </div>
            )}
          </div>
        )}
        <LeagueTable table={table} myName={myName}/>
        <p style={{fontSize:'0.64rem',color:'#4a6a9e',textAlign:'center'}}>
          Session open 24h · Code <strong style={{color:'#e8002d'}}>{sessionCode}</strong>
        </p>
        <button onClick={onBack} style={{padding:'11px',border:'none',borderRadius:10,width:'100%',
          background:'linear-gradient(135deg,#ffd700,#c9a012)',color:'#080f1e',
          fontFamily:"'Bebas Neue',sans-serif",fontSize:'1.2rem',letterSpacing:'0.1em',cursor:'pointer'}}>
          Back to Menu
        </button>
      </div>
    </div>
  );

  return null;
}
